// Discord-flavoured markdown: one renderer for the site and the Discord preview, and an editor with a formatting toolbar.
// Builds React nodes only; untrusted text never becomes HTML.
import { Fragment, useLayoutEffect, useRef, type KeyboardEvent, type ReactNode } from "react";
import { cn, Textarea } from "./index.tsx";

type Tone = "site" | "discord";

const STYLE: Record<Tone, Record<"h1" | "h2" | "h3" | "strong" | "code" | "pre" | "link" | "quote" | "small" | "spoiler" | "list", string>> = {
  site: {
    h1: "text-xl font-bold text-fg", h2: "text-lg font-bold text-fg", h3: "text-base font-semibold text-fg",
    strong: "font-semibold text-fg", code: "rounded bg-raised px-1 py-px font-mono text-[0.85em] text-fg",
    pre: "rounded-lg border border-line bg-bg/70 p-3 font-mono text-xs text-fg", link: "text-accent hover:underline",
    quote: "border-l-2 border-line-strong pl-3", small: "text-xs text-faint", spoiler: "rounded bg-fg/80 px-0.5 text-transparent hover:bg-transparent hover:text-inherit",
    list: "marker:text-faint",
  },
  discord: {
    h1: "text-[1.5em] font-bold text-white", h2: "text-[1.25em] font-bold text-white", h3: "text-[1.05em] font-bold text-white",
    strong: "font-semibold text-white", code: "rounded bg-[#1e1f22] px-1 py-px font-mono text-[0.85em] text-[#dbdee1]",
    pre: "rounded border border-[#1e1f22] bg-[#1e1f22] p-2 font-mono text-[12px] text-[#dbdee1]", link: "text-[#00a8fc] hover:underline",
    quote: "border-l-4 border-[#4e5058] pl-3", small: "text-[12px] text-[#949ba4]", spoiler: "rounded bg-[#1e1f22] px-0.5 text-transparent hover:bg-[#1e1f22]/40 hover:text-inherit",
    list: "marker:text-[#949ba4]",
  },
};

// Order matters: code spans first so nothing inside them is formatted, ** before *, __ before _.
const INLINE = /(`[^`\n]+`|\*\*[^\n]+?\*\*|__[^\n]+?__|~~[^\n]+?~~|\|\|[^\n]+?\|\||\*[^*\n]+\*|_[^_\n]+_|\[[^\]\n]+\]\(https?:\/\/[^)\s]+\)|https?:\/\/[^\s<>()]+)/g;

function inline(text: string, s: (typeof STYLE)[Tone], key: string): ReactNode[] {
  const out: ReactNode[] = [];
  let last = 0;
  for (const m of text.matchAll(INLINE)) {
    const t = m[0];
    const at = m.index!;
    const k = `${key}.${at}`;
    if (at > last) out.push(text.slice(last, at));
    last = at + t.length;
    if (t.startsWith("`")) out.push(<code key={k} className={s.code}>{t.slice(1, -1)}</code>);
    else if (t.startsWith("**")) out.push(<strong key={k} className={s.strong}>{inline(t.slice(2, -2), s, k)}</strong>);
    else if (t.startsWith("__")) out.push(<u key={k}>{inline(t.slice(2, -2), s, k)}</u>);
    else if (t.startsWith("~~")) out.push(<s key={k}>{inline(t.slice(2, -2), s, k)}</s>);
    else if (t.startsWith("||")) out.push(<span key={k} className={s.spoiler}>{inline(t.slice(2, -2), s, k)}</span>);
    else if (t.startsWith("[")) {
      const [, label, url] = /^\[([^\]]+)\]\(([^)]+)\)$/.exec(t)!;
      out.push(<a key={k} href={url} target="_blank" rel="noreferrer noopener" className={s.link}>{inline(label!, s, k)}</a>);
    } else if (t.startsWith("http")) out.push(<a key={k} href={t} target="_blank" rel="noreferrer noopener" className={s.link}>{t}</a>);
    else out.push(<em key={k}>{inline(t.slice(1, -1), s, k)}</em>);
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
}

type Block =
  | { kind: "h"; level: 1 | 2 | 3; text: string }
  | { kind: "small" | "line"; text: string }
  | { kind: "blank" }
  | { kind: "pre"; text: string }
  | { kind: "quote"; lines: string[] }
  | { kind: "list"; ordered: boolean; start: number; items: { text: string; depth: number }[] };

const BULLET = /^(\s*)[-*] (.*)$/;
const NUMBERED = /^(\s*)(\d{1,3})\. (.*)$/;

function parse(src: string): Block[] {
  const lines = src.replace(/\r\n/g, "\n").split("\n");
  const blocks: Block[] = [];
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i]!;
    if (line.startsWith("```")) {
      const body: string[] = [];
      for (i++; i < lines.length && !lines[i]!.startsWith("```"); i++) body.push(lines[i]!);
      blocks.push({ kind: "pre", text: body.join("\n") });
      continue;
    }
    if (line.startsWith(">>> ")) { blocks.push({ kind: "quote", lines: [line.slice(4), ...lines.slice(i + 1)] }); break; }
    if (line.startsWith("> ") || line === ">") {
      const q: string[] = [];
      for (; i < lines.length && (lines[i]!.startsWith("> ") || lines[i] === ">"); i++) q.push(lines[i]!.slice(2));
      i--;
      blocks.push({ kind: "quote", lines: q });
      continue;
    }
    const h = /^(#{1,3}) (.+)$/.exec(line);
    if (h) { blocks.push({ kind: "h", level: h[1]!.length as 1 | 2 | 3, text: h[2]! }); continue; }
    if (line.startsWith("-# ")) { blocks.push({ kind: "small", text: line.slice(3) }); continue; }
    const first = BULLET.exec(line) ?? NUMBERED.exec(line);
    if (first) {
      const ordered = NUMBERED.test(line) && !BULLET.test(line);
      const items: { text: string; depth: number }[] = [];
      for (; i < lines.length; i++) {
        const m = ordered ? NUMBERED.exec(lines[i]!) : BULLET.exec(lines[i]!);
        if (!m) break;
        items.push({ depth: Math.min(2, Math.floor(m[1]!.length / 2)), text: m[m.length - 1]! });
      }
      i--;
      blocks.push({ kind: "list", ordered, start: ordered ? Number(NUMBERED.exec(line)![2]) : 1, items });
      continue;
    }
    blocks.push(line.trim() === "" ? { kind: "blank" } : { kind: "line", text: line });
  }
  return blocks;
}

export function Markdown({ text, tone = "site", className }: { text: string; tone?: Tone; className?: string }) {
  const s = STYLE[tone];
  return (
    <div className={cn("break-words", className)}>
      {parse(text).map((b, i) => {
        const k = String(i);
        switch (b.kind) {
          case "h": return <div key={k} className={cn(s[`h${b.level}`], "mb-1 mt-2 leading-tight first:mt-0")}>{inline(b.text, s, k)}</div>;
          case "small": return <div key={k} className={s.small}>{inline(b.text, s, k)}</div>;
          case "blank": return <div key={k} className="h-[0.6em]" />;
          case "pre": return <pre key={k} className={cn(s.pre, "my-1 overflow-x-auto whitespace-pre")}>{b.text}</pre>;
          case "quote": return <div key={k} className={cn(s.quote, "my-0.5")}>{b.lines.map((l, j) => <div key={j}>{l ? inline(l, s, `${k}.${j}`) : <br />}</div>)}</div>;
          case "list": {
            const List = b.ordered ? "ol" : "ul";
            return (
              <List key={k} start={b.ordered ? b.start : undefined} className={cn(s.list, "my-0.5 pl-5", b.ordered ? "list-decimal" : "list-disc")}>
                {b.items.map((it, j) => <li key={j} style={{ marginLeft: `${it.depth * 1.25}rem` }} className={it.depth ? "list-[circle]" : undefined}>{inline(it.text, s, `${k}.${j}`)}</li>)}
              </List>
            );
          }
          default: return <div key={k}>{inline(b.text, s, k)}</div>;
        }
      })}
    </div>
  );
}

// ── Editor ──

type Edit = { value: string; start: number; end: number };

function wrap(e: Edit, before: string, after: string, placeholder: string): Edit {
  const sel = e.value.slice(e.start, e.end);
  // Toggle off when the selection is already wrapped.
  if (e.value.slice(e.start - before.length, e.start) === before && e.value.slice(e.end, e.end + after.length) === after) {
    return { value: e.value.slice(0, e.start - before.length) + sel + e.value.slice(e.end + after.length), start: e.start - before.length, end: e.end - before.length };
  }
  const inner = sel || placeholder;
  return { value: e.value.slice(0, e.start) + before + inner + after + e.value.slice(e.end), start: e.start + before.length, end: e.start + before.length + inner.length };
}

/** Adds (or, if every line already has it, removes) a prefix on each selected line. `prefix` gets the line's index for numbered lists. */
function prefixLines(e: Edit, prefix: (i: number) => string, match: RegExp): Edit {
  const from = e.value.lastIndexOf("\n", e.start - 1) + 1;
  const nl = e.value.indexOf("\n", e.end);
  const to = nl === -1 ? e.value.length : nl;
  const lines = e.value.slice(from, to).split("\n");
  const all = lines.every((l) => match.test(l));
  const next = lines.map((l, i) => (all ? l.replace(match, "") : prefix(i) + l.replace(/^(#{1,3} |-# |> |[-*] |\d{1,3}\. )/, ""))).join("\n");
  return { value: e.value.slice(0, from) + next + e.value.slice(to), start: from, end: from + next.length };
}

const TOOLS: { label: ReactNode; title: string; key?: string; run: (e: Edit) => Edit }[][] = [
  [
    { label: <b>B</b>, title: "Bold (Ctrl+B)", key: "b", run: (e) => wrap(e, "**", "**", "bold text") },
    { label: <i className="font-serif">I</i>, title: "Italic (Ctrl+I)", key: "i", run: (e) => wrap(e, "*", "*", "italic text") },
    { label: <u>U</u>, title: "Underline (Ctrl+U)", key: "u", run: (e) => wrap(e, "__", "__", "underlined") },
    { label: <s>S</s>, title: "Strikethrough", run: (e) => wrap(e, "~~", "~~", "struck out") },
    { label: <span className="font-mono">{"</>"}</span>, title: "Inline code", key: "e", run: (e) => wrap(e, "`", "`", "code") },
    { label: <span className="tracking-tighter">▮▮</span>, title: "Spoiler", run: (e) => wrap(e, "||", "||", "hidden") },
    {
      label: "Link", title: "Link (Ctrl+K)", key: "k",
      run: (e) => {
        const text = e.value.slice(e.start, e.end) || "link text";
        const v = `${e.value.slice(0, e.start)}[${text}](https://)${e.value.slice(e.end)}`;
        const url = e.start + text.length + 3;
        return { value: v, start: url, end: url + 8 };
      },
    },
  ],
  [
    { label: "H1", title: "Big heading", run: (e) => prefixLines(e, () => "# ", /^# /) },
    { label: "H2", title: "Heading", run: (e) => prefixLines(e, () => "## ", /^## /) },
    { label: "H3", title: "Small heading", run: (e) => prefixLines(e, () => "### ", /^### /) },
    { label: "• List", title: "Bulleted list", run: (e) => prefixLines(e, () => "- ", /^[-*] /) },
    { label: "1. List", title: "Numbered list", run: (e) => prefixLines(e, (i) => `${i + 1}. `, /^\d{1,3}\. /) },
    { label: "❝ Quote", title: "Quote", run: (e) => prefixLines(e, () => "> ", /^> ?/) },
    { label: "Small", title: "Small grey text", run: (e) => prefixLines(e, () => "-# ", /^-# /) },
    {
      label: <span className="font-mono">{"{ }"}</span>, title: "Code block",
      run: (e) => {
        const r = wrap(e, "```\n", "\n```", "code");
        // Code fences must start on their own line.
        if (r.start - 4 > 0 && r.value[r.start - 5] !== "\n") return { value: r.value.slice(0, r.start - 4) + "\n" + r.value.slice(r.start - 4), start: r.start + 1, end: r.end + 1 };
        return r;
      },
    },
  ],
];

/** Textarea with a formatting toolbar, Ctrl shortcuts, and lists that continue on Enter. */
export function MarkdownEditor({ value, onChange, maxLength, rows = 8, placeholder }: {
  value: string; onChange: (v: string) => void; maxLength?: number; rows?: number; placeholder?: string;
}) {
  const ref = useRef<HTMLTextAreaElement>(null);
  // Selection to restore once React has committed the edited value; doing it later lets fast typing land in the wrong place.
  const pending = useRef<[number, number] | null>(null);
  useLayoutEffect(() => {
    if (!pending.current || !ref.current) return;
    ref.current.focus();
    ref.current.setSelectionRange(...pending.current);
    pending.current = null;
  }, [value]);

  const apply = (fn: (e: Edit) => Edit) => {
    const el = ref.current;
    if (!el) return;
    const r = fn({ value, start: el.selectionStart, end: el.selectionEnd });
    if (maxLength && r.value.length > maxLength) return;
    pending.current = [r.start, r.end];
    onChange(r.value);
  };

  const onKeyDown = (ev: KeyboardEvent<HTMLTextAreaElement>) => {
    if ((ev.ctrlKey || ev.metaKey) && !ev.shiftKey && !ev.altKey) {
      const tool = TOOLS.flat().find((t) => t.key === ev.key.toLowerCase());
      if (tool) { ev.preventDefault(); apply(tool.run); }
      return;
    }
    if (ev.key !== "Enter" || ev.shiftKey) return;
    const el = ev.currentTarget;
    if (el.selectionStart !== el.selectionEnd) return;
    const pos = el.selectionStart;
    const from = value.lastIndexOf("\n", pos - 1) + 1;
    const line = value.slice(from, pos);
    const m = /^(\s*)([-*]|\d{1,3}\.|>) (.*)$/.exec(line);
    if (!m) return;
    ev.preventDefault();
    if (!m[3]) { apply(() => ({ value: value.slice(0, from) + value.slice(pos), start: from, end: from })); return; } // empty item ends the list
    const marker = /^\d/.test(m[2]!) ? `${parseInt(m[2]!, 10) + 1}.` : m[2]!;
    const insert = `\n${m[1]}${marker} `;
    apply((e) => ({ value: e.value.slice(0, pos) + insert + e.value.slice(pos), start: pos + insert.length, end: pos + insert.length }));
  };

  return (
    <div className="overflow-hidden rounded-lg border border-line focus-within:border-accent/60">
      <div className="flex flex-wrap items-center gap-0.5 border-b border-line bg-raised/60 px-1.5 py-1">
        {TOOLS.map((group, g) => (
          <Fragment key={g}>
            {g > 0 && <span className="mx-1 h-4 w-px bg-line" />}
            {group.map((t) => (
              <button key={t.title} type="button" title={t.title} aria-label={t.title} onMouseDown={(e) => e.preventDefault()} onClick={() => apply(t.run)}
                className="inline-flex h-7 min-w-7 items-center justify-center rounded px-1.5 text-xs text-muted transition-colors hover:bg-hover hover:text-fg">
                {t.label}
              </button>
            ))}
          </Fragment>
        ))}
      </div>
      <Textarea ref={ref} value={value} onChange={(e) => onChange(e.target.value)} onKeyDown={onKeyDown} maxLength={maxLength} rows={rows}
        placeholder={placeholder} className="rounded-none! border-0! shadow-none! font-mono text-[13px] focus:ring-0!" />
    </div>
  );
}
